/**
 * Task queue grouping – pure. The owner works a stack of similar tasks in one
 * go, so the To-do list renders groups instead of one list, with the rules of
 * the Copilot extension's inbox (`apps/jarvis-copilot/src/panel/inbox.ts`):
 *
 * - the owner's own tasks (no proposing employee) form one group "Your own";
 * - team requests (`audience = "team"`) stand apart and are never batched;
 * - a task with a `batch` joins its batch group – a campaign bundle's tasks
 *   carry the batch key `bundle-<bundle uuid hex>`;
 * - every other task is grouped by kind: LinkedIn messages · calls · other.
 *
 * The server sends the items sorted by urgency (soonest expiry, priority,
 * oldest), so a group's place is where its most urgent task first appears.
 */
import type { HumanTask, TaskBatchProgress } from "../api/tasks";
import { bundleUrl, campaignUrl } from "./console";

export type TaskKind = "linkedin" | "call" | "other";

export interface TaskGroup {
  /** Batch key, `kind:<kind>`, `own` or `team` – the `?work=` value. */
  key: string;
  type: "batch" | "kind" | "own" | "team";
  label: string;
  /** Set for a campaign bundle's batch. */
  bundleId: string | null;
  campaignSlug: string | null;
  campaignName: string | null;
  /** The group's open tasks in work order. */
  tasks: HumanTask[];
  /** Server progress of a batch: sent/done out of all its tasks; null for other groups. */
  progress: { done: number; total: number } | null;
  /** The rounds the owner sent back and the seat has not answered yet. */
  revising: RevisingRound[];
}

/** One open round of a group – its note, since when, which seat, how many tasks. */
export interface RevisingRound {
  roundId: string;
  note: string;
  since: string | null;
  seat: string | null;
  count: number;
}

/** The owner sent the task back and the seat has not answered yet. */
export function isBeingRevised(task: Pick<HumanTask, "revision">): boolean {
  return !!task.revision && !task.revision.released;
}

/** The open rounds among a group's tasks, oldest first. Pure. */
export function revisingRounds(tasks: HumanTask[]): RevisingRound[] {
  const rounds = new Map<string, RevisingRound>();
  for (const task of tasks) {
    if (!isBeingRevised(task) || !task.revision) continue;
    const r = task.revision;
    const round = rounds.get(r.round_id) ?? { roundId: r.round_id, note: r.note, since: r.since, seat: r.seat, count: 0 };
    round.count += 1;
    rounds.set(r.round_id, round);
  }
  return [...rounds.values()].sort((a, b) => (a.since ?? "").localeCompare(b.since ?? ""));
}

const TASK_TYPE_LABEL: Record<string, string> = {
  linkedin_message: "LinkedIn message",
  linkedin: "LinkedIn",
  follow_up_call: "Call",
  follow_up_email: "Email",
  meeting: "Meeting",
  review: "Review",
  reminder: "Reminder",
  other: "Task",
};

const KIND_LABEL: Record<TaskKind, string> = {
  linkedin: "LinkedIn messages",
  call: "Calls",
  other: "Other tasks",
};

export function taskTypeLabel(taskType: string | null | undefined): string {
  return TASK_TYPE_LABEL[taskType ?? ""] ?? "Task";
}

/** Sending a LinkedIn message is the act – the close reads "Sent ✓". */
export function isMessageTask(task: Pick<HumanTask, "task_type" | "message_url">): boolean {
  return task.task_type === "linkedin_message" || Boolean(task.message_url);
}

export function taskKind(task: Pick<HumanTask, "task_type" | "message_url" | "linkedin_url">): TaskKind {
  if (isMessageTask(task) || task.task_type === "linkedin" || task.linkedin_url) return "linkedin";
  if (task.task_type === "follow_up_call") return "call";
  return "other";
}

/** "Who": the person, else the task title. */
export function taskWho(task: HumanTask): string {
  return task.contact_name || task.title || taskTypeLabel(task.task_type);
}

/** The work-through key of a campaign bundle's tasks. */
export function bundleBatchKey(bundleId: string): string {
  return `bundle-${bundleId.replace(/-/g, "").toLowerCase()}`;
}

/** The group of the owner's own tasks. */
export const OWN_KEY = "own";

export function groupTasks(
  items: HumanTask[],
  batches: TaskBatchProgress[] = [],
): { groups: TaskGroup[]; team: HumanTask[] } {
  const byKey = new Map<string, TaskGroup>();
  const order: string[] = [];
  const team: HumanTask[] = [];
  const progressOf = new Map(batches.map((b) => [b.key, b]));
  for (const task of items) {
    if (!task.agent_id) {
      if (!byKey.has(OWN_KEY)) {
        byKey.set(OWN_KEY, {
          key: OWN_KEY,
          type: "own",
          label: "Your own",
          bundleId: null,
          campaignSlug: null,
          campaignName: null,
          tasks: [],
          progress: null,
          revising: [],
        });
        order.push(OWN_KEY);
      }
      byKey.get(OWN_KEY)!.tasks.push(task);
      continue;
    }
    if (task.audience === "team") {
      team.push(task);
      continue;
    }
    let key: string;
    if (task.batch?.key) {
      key = task.batch.key;
      if (!byKey.has(key)) {
        const p = progressOf.get(key);
        byKey.set(key, {
          key,
          type: "batch",
          label: p?.label || task.batch.label || key,
          bundleId: p?.bundle_id ?? task.bundle_id ?? null,
          campaignSlug: p?.campaign_slug ?? task.campaign_slug ?? null,
          campaignName: p?.campaign_name ?? null,
          tasks: [],
          progress: p && p.total > 0 ? { done: p.completed, total: p.total } : null,
          revising: [],
        });
        order.push(key);
      }
    } else {
      const kind = taskKind(task);
      key = `kind:${kind}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          type: "kind",
          label: KIND_LABEL[kind],
          bundleId: null,
          campaignSlug: null,
          campaignName: null,
          tasks: [],
          progress: null,
          revising: [],
        });
        order.push(key);
      }
    }
    byKey.get(key)!.tasks.push(task);
  }
  // Tasks being revised go last – their draft is about to change.
  const groups = order.map((k) => {
    const group = byKey.get(k)!;
    const tasks = [...group.tasks.filter((t) => !isBeingRevised(t)), ...group.tasks.filter(isBeingRevised)];
    return { ...group, tasks, revising: revisingRounds(group.tasks) };
  });
  return { groups, team };
}

/** The group a `?work=` key names, the team requests included. */
export function findGroup(items: HumanTask[], batches: TaskBatchProgress[], key: string): TaskGroup | null {
  const { groups, team } = groupTasks(items, batches);
  if (key === "team") {
    return team.length
      ? { key: "team", type: "team", label: "Team requests", bundleId: null, campaignSlug: null, campaignName: null, tasks: team, progress: null, revising: [] }
      : null;
  }
  return groups.find((g) => g.key === key) ?? null;
}

/** "3 of 8 done" for a batch, "5 open" for a kind group. */
export function groupProgressText(group: TaskGroup): string {
  if (group.progress) return `${group.progress.done} of ${group.progress.total} done`;
  return `${group.tasks.length} open`;
}

/** The campaign page of a bundle group, else null. */
export function groupCampaignHref(group: Pick<TaskGroup, "bundleId" | "campaignSlug">): string | null {
  if (!group.campaignSlug) return null;
  return group.bundleId ? bundleUrl(group.campaignSlug, group.bundleId) : campaignUrl(group.campaignSlug);
}
